import { DateTime } from '@ksyos/date-time';
import { Alert, Box, Button, ButtonRow } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useContext, useEffect, useState } from 'react';
import { DatePickerFormGroupField } from '@frontend/components/fields/DatePickerField';
import { TextInputFormGroupField } from '@frontend/components/fields/TextInputField';
import { getSelectedLanguage } from '@frontend/translate';
import type { ValidationErrors } from '../../../../utils/validation';
import type { IPatientSearchResult } from './PatientSearchStartBar';
import { patientsSearchSchema } from '../../../../../common/validation/patient';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../../utils/validation';
import { FormContextProvider } from '../../../elements/formContext';
import { PatientSearchContext, SearchType } from './patientSearchContext';

enum MissingSearchFieldScenarios {
    birthDate = 'birthDate',
    both = 'both',
    identifierSocialSecurity = 'identifierSocialSecurity',
}

export function PatientEnterExtraInformationBar() {
    const { patient, message, search, setPatient } = useContext(PatientSearchContext);
    const translate = getSelectedLanguage();
    const navigate = useNavigate();

    const [missingSearchField, setMissingSearchFields] = useState<MissingSearchFieldScenarios>();
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);

    useEffect(() => {
        setMissingSearchFields(getMissingSearchFields());
    }, []);

    return (
        <FormContextProvider
            value={{
                errors: errors,
                item: patient,
                onChange: handleChange,
                onDoValidate: handleValidation,
                placeholderGroupName: 'patient',
            }}
        >
            <Box
                pb={6}
                px={6}
            >
                <Alert
                    appearance="info"
                    noMargin={true}
                >
                    {getSearchText()}
                </Alert>
                <Box
                    display="grid"
                    flex="1"
                    gridTemplateColumns={['1fr', null, null, getGridColumnLayout()]}
                    sx={{ columnGap: 2 }}
                >
                    {renderMissingSearchFields()}
                    <ButtonRow
                        isStackedBelowSmall={true}
                        sx={{ alignItems: [null, 'flex-start'], justifyContent: 'space-between' }}
                    >
                        <Button
                            className="automation-patientSearchBarButtonSearch"
                            color="secondary"
                            onClick={() => handleSearch()}
                            variant="outline"
                        >
                            {translate.patient.createPatient}
                        </Button>
                        {missingSearchField !== MissingSearchFieldScenarios.birthDate && (
                            <Button
                                onClick={handlePatientNoBsnClick}
                                variant="link"
                            >
                                {translate.patient.noBSN}
                            </Button>
                        )}
                    </ButtonRow>
                </Box>
            </Box>
        </FormContextProvider>
    );

    function handlePatientNoBsnClick() {
        patient.birthDate
            ? navigate(`/patients/noBsn?birthDate=${DateTime.from(patient.birthDate).format('YYYY-MM-DD')}`)
            : navigate('/patients/noBsn');
    }

    function handleSearch() {
        if (!patient) {
            return;
        }
        const patientToValidate =
            patient.birthDate !== null
                ? { ...patient, birthDate: DateTime.from(patient.birthDate).format('YYYY-MM-DD') }
                : patient;
        const { errors: validationErrors } = validate(patientsSearchSchema, patientToValidate);
        setErrors(validationErrors);
        if (validationErrors.size > 0) {
            return;
        }

        search(SearchType.extraInformationBar, patientToValidate);
    }

    function renderMissingSearchFields() {
        switch (missingSearchField) {
            case MissingSearchFieldScenarios.birthDate: {
                return (
                    <DatePickerFormGroupField
                        color="secondary"
                        fieldKey="birthDate"
                        label=""
                        layout="vertical"
                        maxValue={DateTime.now().toDate()}
                    />
                );
            }
            case MissingSearchFieldScenarios.identifierSocialSecurity: {
                return (
                    <TextInputFormGroupField
                        dontValidateOnChange={true}
                        error={message}
                        fieldKey="identifierSocialSecurity"
                        label=""
                        layout="vertical"
                        onEnterKeyPress={() => handleSearch()}
                        placeholder={translate.patient.identifierSocialSecurity}
                    />
                );
            }
            default: {
                return (
                    <>
                        <TextInputFormGroupField
                            dontValidateOnChange={true}
                            error={message}
                            fieldKey="identifierSocialSecurity"
                            label=""
                            layout="vertical"
                            onEnterKeyPress={() => handleSearch()}
                            placeholder={translate.patient.identifierSocialSecurity}
                        />
                        <DatePickerFormGroupField
                            color="secondary"
                            fieldKey="birthDate"
                            label=""
                            layout="vertical"
                            maxValue={DateTime.now().toDate()}
                        />
                    </>
                );
            }
        }
    }

    function handleChange<K extends keyof IPatientSearchResult>(name: K, value: IPatientSearchResult[K]) {
        const cleanedValue =
            name === 'birthDate' && typeof value === 'string' ? DateTime.from(value).format('YYYY-MM-DD') : value;
        if (patient[name] !== cleanedValue) {
            const updatedPatient = { ...patient, [name]: cleanedValue };
            setPatient({ ...updatedPatient });
        }
    }

    function handleValidation(key: keyof IPatientSearchResult) {
        if (key === 'birthDate') {
            const dateToValidate = patient.birthDate ? DateTime.from(patient.birthDate).format('YYYY-MM-DD') : null;
            setErrors(
                updateValidationErrorsForOne(
                    patientsSearchSchema,
                    { ...patient, birthDate: dateToValidate },
                    errors,
                    'birthDate'
                )
            );

            if (dateToValidate && DateTime.from(dateToValidate).isAfter(DateTime.now(), 'day')) {
                setErrors(errors.set('birthDate', 'date_future'));
            }
        }
        if (key !== 'birthDate') {
            validateOne(key);
        }
    }

    function validateOne(name: keyof IPatientSearchResult) {
        setErrors(updateValidationErrorsForOne(patientsSearchSchema, patient, errors, name));
    }

    function getGridColumnLayout() {
        switch (missingSearchField) {
            case MissingSearchFieldScenarios.birthDate: {
                return 'min-content minmax(0, 1fr)';
            }
            case MissingSearchFieldScenarios.identifierSocialSecurity: {
                return 'minmax(0, 256px) minmax(0, 1fr)';
            }
            default: {
                return 'minmax(0, 256px) min-content minmax(0, 1fr)';
            }
        }
    }

    function getSearchText() {
        let searchText = translate.patient.createNewPatient;
        switch (missingSearchField) {
            case MissingSearchFieldScenarios.birthDate: {
                return (searchText += ' ' + translate.patient.addBirthDate);
            }
            case MissingSearchFieldScenarios.identifierSocialSecurity: {
                return (searchText += ' ' + translate.patient.addBSN);
            }
            default: {
                return (searchText += ' ' + translate.patient.addBSNAndBirthDate);
            }
        }
    }

    function getMissingSearchFields() {
        const hasBirthDate = patient.birthDate;
        const hasIdentifierSocialSecurity = patient.identifierSocialSecurity;

        if (!hasBirthDate && !hasIdentifierSocialSecurity) {
            return MissingSearchFieldScenarios.both;
        } else if (!hasBirthDate) {
            return MissingSearchFieldScenarios.birthDate;
        } else {
            return MissingSearchFieldScenarios.identifierSocialSecurity;
        }
    }
}
